import React, { useState, useEffect, useMemo } from 'react';
import { Grommet, Box, Notification as GrommetNotification } from 'grommet';
import { hpe } from 'grommet-theme-hpe';
import { deepMerge } from 'grommet/utils';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

// Import the CSS file for safe-area logic
import './utils/App.css';

import { Header, Notification, AgentSidebar } from './components';
import { Home } from './pages/home';
import { DataScienceHome } from './pages/data-science';
import { ProcessHome } from './pages/process';
import { CoInnovationHome } from './pages/co-innovation';
import { ResearchHome } from './pages/research';
import { AgentHome } from './pages/agent';
import { SmartSearchDemo } from './pages/demos/smart-search';
import { DMaaSDemo } from './pages/demos/dmaas';
import { VoiceDemo } from './pages/demos/voice';
import { FeedbackHome } from './pages/feedback';
import { BiLabInfoHome } from './pages/bi-lab-info';

import { HeaderHeightProvider } from './utils/HeaderHeightContext';

function App() {
  const [isAgentExpanded, setIsAgentExpanded] = useState(false);
  const [showSoonToast, setShowSoonToast] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  const customTheme = useMemo(
    () =>
      deepMerge(hpe, {
        global: {
          hover: {
            background: {
              color: 'background-contrast',
              opacity: '100%',
            },
          },
        },
      }),
    []
  );

  useEffect(() => {
    const handleTriggerSoon = () => setShowSoonToast(true);
    window.addEventListener('show-coming-soon', handleTriggerSoon);

    const darkQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setIsDarkMode(e.matches);
    darkQuery.addEventListener('change', handleChange);

    return () => {
      window.removeEventListener('show-coming-soon', handleTriggerSoon);
      darkQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const toggleAgent = () => setIsAgentExpanded((prev) => !prev);

  const currentBackgroundImage = isDarkMode
    ? '/images/background_dark.jpg'
    : '/images/background.jpg';

  return (
    <HeaderHeightProvider>
      <BrowserRouter>
        <Grommet
          className="App"
          theme={customTheme}
          themeMode={isDarkMode ? 'dark' : 'light'}
          full
          background="background"
          style={{
            backgroundColor: 'transparent',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* BACKGROUND WALLPAPER — Always true edge-to-edge */}
          <Box
            fill
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: -1,
              backgroundImage: `url(${currentBackgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />

          {/* MAIN WRAPPER — Handles Safe Area Padding via App.css */}
          <Box fill direction="column" className="App-Container">
            {showSoonToast && (
              <GrommetNotification
                toast={{ position: 'center' }}
                status="info"
                title="Coming Soon"
                onClose={() => setShowSoonToast(false)}
              />
            )}

            {/* App Header */}
            <Box flex={false} background="transparent">
              <Header toggleAgent={toggleAgent} />
            </Box>

            {/* Main content area */}
            <Box flex background="transparent" direction="column">
              <Notification />

              <Box direction="row" flex margin={{ top: 'xxsmall' }}>
                <Box flex className="main-content-area">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/data-science" element={<DataScienceHome />} />
                    <Route path="/process" element={<ProcessHome />} />
                    <Route path="/agent" element={<AgentHome />} />
                    <Route path="/co-innovation" element={<CoInnovationHome />} />
                    <Route
                      path="/research/:tabId?"
                      element={<ResearchHome />}
                    />
                    <Route
                      path="/demos/smart-search"
                      element={<SmartSearchDemo />}
                    />
                    <Route path="/demos/dmaas" element={<DMaaSDemo />} />
                    <Route path="/demos/voice" element={<VoiceDemo />} />
                    <Route path="/feedback" element={<FeedbackHome />} />
                    <Route
                      path="/bi-lab-info"
                      element={<BiLabInfoHome />}
                    />
                    <Route path="*" element={<Home />} />
                  </Routes>
                </Box>

                {/* AgentSidebar — Will slide in relative to the safe-area padded container */}
                <AgentSidebar isAgentExpanded={isAgentExpanded} />
              </Box>
            </Box>
          </Box>
        </Grommet>
      </BrowserRouter>
    </HeaderHeightProvider>
  );
}

export default App;
